import { Button } from '~/components/ui/button';
import { reconcileView, type CheckoutSide } from './pro-builder-checkout-choice';
import type { GitNeedsAction } from './publish-stream';

/**
 * FRONT-3411: the reconcile buttons of a Git review, with what the local files
 * are (judged by content) and what each choice does. The Save App dialog and the terminal screen
 * both render this, so the same review reads the same in both places.
 */
export function ReconcileChoice({ action, busy, onChoose, autofocus = false }: { action: GitNeedsAction; busy: boolean; onChoose: (choice: CheckoutSide) => unknown; autofocus?: boolean }) {
  const view = reconcileView(action);
  if (!view.options.length) return null;
  return (
    <div data-testid="pro-builder-reconcile-choice" className="flex flex-col gap-2">
      {view.history && <p data-testid="pro-builder-checkout-history" className="text-sm">{view.history}</p>}
      <div className="flex flex-wrap gap-2">
        {view.options.map((option) => (
          <Button key={option.choice} type="button" size="sm" variant={option.primary ? 'default' : 'outline'} disabled={busy} data-choice={option.choice} {...(autofocus && option.primary ? { 'data-pro-builder-autofocus': '' } : {})} onClick={() => onChoose(option.choice)}>
            {option.label}
          </Button>
        ))}
      </div>
      {view.options.some((option) => option.detail) && (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {view.options.map((option) => option.detail && <li key={option.choice}><span className="font-medium text-foreground">{option.label}:</span> {option.detail}</li>)}
        </ul>
      )}
    </div>
  );
}

/** The files a review names, each with both versions when they can be shown. */
export function GitVersionsList({ action }: { action: GitNeedsAction }) {
  return <>
    <ul>{action.paths.map((path) => <li key={path} className="break-all font-mono text-xs">{path}</li>)}</ul>
    {action.versions?.map((version) => <details key={version.path}><summary className="break-all">Review versions: {version.path}</summary>
      {version.binary ? <p>Binary file — content is not shown.</p> : <div className="grid grid-cols-2 gap-2">{(['local', 'current'] as const).map((kind) => <div key={kind}><p>{kind === 'local' ? 'My local version' : 'Current app version'}</p><pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs">{version[kind] === null ? 'Missing or deleted' : version[kind]}</pre></div>)}</div>}
      {version.truncated && <p>Preview truncated: only the start of each version is shown.</p>}
    </details>)}
  </>;
}

export function GitActionDetails({ action }: { action: GitNeedsAction }) {
  return <div role="alert" className="flex flex-col gap-2">
    <p>{action.message}</p>
    <GitVersionsList action={action} />
  </div>;
}
